/**
 * Calendar dates, compared as calendar dates.
 *
 * This exists because of a bug that was in eight places at once:
 *
 *   new Date(quote.validUntil) < new Date()
 *
 * `validUntil` is a calendar date like "2026-10-15". `new Date()` of that is
 * **UTC midnight**, so from 00:00 UTC on the 15th the comparison is already
 * true — which is 5pm on the 14th in California. A quote showed as expired on
 * its last valid day, and an invoice showed OVERDUE the evening before it was
 * due. The backend disagreed, because it compares YYYY-MM-DD strings properly.
 *
 * A date with no time is not an instant, and should never be turned into one
 * to answer "is it past yet". Compare the strings.
 */

/** The viewer's today, as YYYY-MM-DD. */
export function today() {
  // en-CA gives YYYY-MM-DD, and no timeZone option means the browser's own —
  // which is exactly the right zone for something a person is reading.
  return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

/** The calendar date a value names, without going near an instant. */
function toDateOnly(value) {
  if (!value) return null;
  if (typeof value === 'string') {
    const m = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
    if (m) return m[1];
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
}

/**
 * Is that date in the past — strictly, by the viewer's calendar?
 *
 * The day itself is NOT past. A quote valid until the 15th is still valid all
 * of the 15th, and an invoice due the 15th is not overdue until the 16th.
 * This matches the backend's `isQuoteExpired`, which the frontend used to
 * contradict.
 */
export function isPastDate(value) {
  const date = toDateOnly(value);
  if (!date) return false;
  return today() > date;
}
